import { Navigation, Params } from 'navi';
import AES from 'crypto-js/aes';
import { Payment, User } from '@repo-level/core-front';
import {
  ifElse,
  map,
  pipe,
  isEmpty as _isEmpty,
  always,
  groupWith,
  curry,
  allPass,
} from 'ramda';
import * as CryptoJS from 'crypto-js';

import { BonusGroup, BonusI, BonusItemGeneral } from 'api/cms/bonuses';
import { CookieType } from 'hooks/common';
import {
  filterByGroupAndDuration,
  sortByOrder,
  filterBySchedule,
  groupsInclude,
  groupsExclude,
} from 'helpers/cms';
import { RangeIP } from './conf';
import { LocalStorage } from './localStorage';

export const removeAllSpecialCharactersFromString = (str: string) => {
  return str.replace(/[^a-zA-Z]/g, '');
};

export const createScript = (url: string) => {
  const script = document.createElement('script');

  script.src = url;
  script.async = true;
  script.type = 'text/javascript';

  document.head.appendChild(script);
};

export const deleteScript = (url: string) => {
  const id = removeAllSpecialCharactersFromString(url);
  const element = document.getElementById(id);

  if (element == null) return;

  element.remove();
};

export const getElementsByClass = (className: string) => {
  const collections = document.getElementsByClassName(className);

  let elementsArray: Element[] = [];

  for (let i = 0; i < collections.length; i++) {
    elementsArray.push(collections[i]);
  }

  return elementsArray;
};

export const createItems = <T>(item: T | undefined, count: number) =>
  Array.apply(null, Array(Math.round(count))).map((_, i) => item || i);

export const filterBonuses = (bonuses: BonusItemGeneral[], user?: User) =>
  pipe(
    filterByGroupAndDuration<BonusItemGeneral>({ user }),
    filterBySchedule,
    sortByOrder
  )(bonuses);

export const findActiveBonuses = (bonuses: BonusItemGeneral[], user?: User) => {
  return pipe<BonusItemGeneral[], BonusItemGeneral[], BonusItemGeneral[]>(
    map((bonus: BonusItemGeneral) => ({
      ...bonus,
      isActive: allPass([groupsInclude(user), groupsExclude(user)])(bonus),
    })),
    sortByOrder
  )(bonuses);
};

export const filterBonusGroups = (bonusGroups: BonusGroup[], user?: User) =>
  pipe<BonusGroup[], BonusGroup[], BonusGroup[], BonusGroup[]>(
    filterByGroupAndDuration<BonusGroup>({ user }),
    filterBySchedule,
    map((group) => ({
      ...group,
      bonuses: findActiveBonuses(group.bonuses, user),
    }))
  )(bonusGroups);

type BonusItemType = BonusI & BonusGroup;

export const splitBonuses = (
  bonuses: Array<BonusItemType>
): Array<BonusI[] & BonusGroup[]> => {
  return ifElse(
    _isEmpty,
    always([]),
    groupWith(
      (bonus1: BonusItemType, bonus2: BonusItemType) =>
        !!bonus1.bonuses === !!bonus2.bonuses
    )
  )(bonuses);
};

export const encrypt = (
  object: object | string | number,
  encryptKey: string
) => {
  const encJson = CryptoJS.AES.encrypt(
    JSON.stringify(object),
    encryptKey
  ).toString();
  const encData = CryptoJS.enc.Base64.stringify(
    CryptoJS.enc.Utf8.parse(encJson)
  );
  return encData;
};

export function decrypt<T = any>(word: string, key: string): T | undefined {
  try {
    const decData = CryptoJS.enc.Base64.parse(word).toString(CryptoJS.enc.Utf8);
    const bytes = CryptoJS.AES.decrypt(decData, key).toString(
      CryptoJS.enc.Utf8
    );
    return JSON.parse(bytes);
  } catch {
    return;
  }
}

const salt = 'lkjh12';

export const encryptUrl = (value: string) => encrypt(value, salt);
export const decryptUrl = (value: string) => decrypt(value, salt);

export function addQueryParameterWithNavigation(
  navigation: Navigation,
  query: Params,
  key: string,
  value: string
) {
  navigation.navigate({
    query: {
      ...query,
      [key]: value,
    },
  });
}

export function removeQueryParameterWithNavigation(
  navigation: Navigation,
  query: Params,
  key: string
) {
  delete query[key];
  navigation.navigate({ query });
}

function getQueryParameterPure(url: string, key: string) {
  const urlObject = new URL(url);
  return urlObject.searchParams.get(key);
}

export function getQueryParameter(key: string) {
  const currentUrl = window.location.href;

  return getQueryParameterPure(currentUrl, key);
}

function addQueryParameterPure(url: string, key: string, value: string) {
  const urlObject = new URL(url);
  urlObject.searchParams.set(key, value);
  return urlObject.href;
}

export function addUrlParameter(key: string, value: string) {
  // add URL parameters without refreshing page

  const currentUrl = window.location.href;

  const newUrl = addQueryParameterPure(currentUrl, key, value);

  if (currentUrl === newUrl) return;

  window.history.pushState({}, document.title, newUrl);
}

function removeQueryParameterPure(url: string, key: string) {
  const urlObject = new URL(url);
  urlObject.searchParams.delete(key);
  return urlObject.href;
}

export function removeUrlParameters(keys: string[]) {
  // remove URL parameters without refreshing page

  const currentUrl = window.location.href;

  const newUrl = keys.reduce(
    (acc, key) => removeQueryParameterPure(acc, key),
    currentUrl
  );

  if (currentUrl === newUrl) return;

  window.history.pushState({}, document.title, newUrl);
}

export const groupOf = curry(<T>(n: number, array: T[]): T[][] =>
  array.reduce(
    (r, e, i) => (i % n ? r[r.length - 1].push(e) : r.push([e])) && r,
    [] as any
  )
);

const ipToNumber = (ip: string) =>
  +ip
    .split('.')
    .map((d) => ('000' + d).substr(-3))
    .join('');

const isIPb6 = (ip: string) => ip.includes(':');

export const checkIpInRange = (blackList: RangeIP[]) => (ip: string) =>
  !isIPb6(ip)
    ? blackList.some(
        ([min, max]) =>
          ipToNumber(min) <= ipToNumber(ip) && ipToNumber(ip) <= ipToNumber(max)
      )
    : false;

export const mergeRefs = (...refs: any[]) => {
  return (node: any) => {
    for (const ref of refs) {
      ref.current = node;
    }
  };
};

export function setCookie(name: string, value: string, days: number) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/';
}

export function getCookie(name: string) {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

export const isEmpty = (value: any) => value == null || value === '';

export const notEmpty = (value: any) => !isEmpty(value);

export const localStorageEmail = new LocalStorage('email', '');

export const canBeConvertedToNumber = (value: string): boolean => {
  const num = +value;

  return !isNaN(num);
};

export const getRandomElementFromList = <T>(list: T[]): T => {
  return list[Math.floor(Math.random() * list.length)];
};

export const randomFromIntervalCrypto = (min: number, max: number) => {
  return Math.random() * (max - min) + min;
};

export const randomIntFromInterval = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

interface ProbabilityOption<T> {
  chanceFrom: number;
  chanceTo: number;
  option: T;
}

export const getResultWithProbability = <T>(
  options: ProbabilityOption<T>[],
  defaultValue: string
) => {
  const randomInt = Math.random() * 100;

  for (const { chanceFrom, chanceTo, option } of options) {
    if (randomInt >= chanceFrom && randomInt <= chanceTo) {
      return option;
    }
  }
  return defaultValue;
};

export interface QuickDepositData {
  amount?: string | null;
  currency?: string | null;
  paymentMethod?: Payment.Method;
}

export const LocalStorageDeposit = new LocalStorage<
  QuickDepositData,
  QuickDepositData
>('deposit', { amount: null, currency: null });
export interface FactoryCookieProps {
  name: string;
  value:
    | {
        type: CookieType.String;
        data: string;
      }
    | {
        type: CookieType.Object;
        data: object | undefined;
      };
  days: number;
  withReplace?: boolean;
  encryptKey?: string;
}

export function factoryCookie({
  name,
  value,
  days,
  withReplace,
  encryptKey,
}: FactoryCookieProps) {
  const data = (() => {
    switch (value.type) {
      case CookieType.String:
        return value.data;

      case CookieType.Object:
        if (value.data == null) return '';
        return JSON.stringify(value.data);

      default:
        return '';
    }
  })();

  if (data === '') return;

  const transData = encryptKey
    ? AES.encrypt(data, encryptKey).toString()
    : data;

  if (withReplace) {
    setCookie(name, transData, days);
    return;
  }

  const valueCookie = getCookie(name);
  if (valueCookie != null) return;
  setCookie(name, transData, days);
}

export const isIos = () => {
  if (typeof window === 'undefined') return false;

  return (
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};
